/**
 * コスト上限関連 Zod スキーマ
 *
 * 月額 API コスト上限の設定・ステータス・管理に関するバリデーションスキーマ。
 * 型定義は z.infer で自動生成し、Single Source of Truth とする。
 */
import { z } from 'zod';
// ========================================
// コスト上限ドメインスキーマ
// ========================================
/**
 * コスト上限値バリデーション（USD 単位、0 以上、小数点以下 2 桁まで）
 */
export const CostLimitUSDSchema = z.number().min(0).multipleOf(0.01);
/**
 * コスト上限設定
 */
export const CostLimitSettingSchema = z.object({
    limitUSD: CostLimitUSDSchema,
    updatedAt: z.string(), // ISO 8601
    updatedBy: z.string(), // admin email
});
/**
 * ユーザー別コスト上限エントリ
 */
export const UserCostLimitEntrySchema = z.object({
    email: z.string(),
    setting: CostLimitSettingSchema,
});
/**
 * コスト上限チェック結果
 */
export const CostLimitCheckResultSchema = z.object({
    allowed: z.boolean(),
    currentCost: z.number(),
    limit: z.number().nullable(), // null = 制限なし
    remaining: z.number().nullable(), // null = 制限なし
    usagePercent: z.number().nullable(), // null = 制限なし
});
/**
 * 使用量期間
 */
export const CostLimitPeriodSchema = z.object({
    year: z.number(),
    month: z.number(),
});
/**
 * システム全体上限の到達段階
 *
 * 通常 / 逼迫（使用率 80% 以上 100% 未満）/ 超過（使用率 100% 以上）の 3 値。
 * 真偽値 2 つで表すと「逼迫かつ超過」という無効な組み合わせが表現できてしまうため列挙とする。
 */
export const SystemCostLimitStageSchema = z.enum(['normal', 'warning', 'exceeded']);
/**
 * システム全体（全ユーザー合計）の当月消化状況
 */
export const SystemCostLimitStatusSchema = z.object({
    /** 当月の全ユーザー合計コスト（USD） */
    currentCost: z.number(),
    /** null = 制限なし */
    limit: z.number().nullable(),
    /** null = 制限なし */
    remaining: z.number().nullable(),
    /** null = 制限なし */
    usagePercent: z.number().nullable(),
    period: CostLimitPeriodSchema,
    /**
     * true = 設定または集計を読み取れず消化状況が不明。
     * このとき currentCost / usagePercent は意味を持たない。
     */
    degraded: z.boolean().optional().default(false),
});
/**
 * 通知済み段階の記録（Requirement 10）
 */
export const SystemCostLimitNotificationStateSchema = z.object({
    /** JST 暦月の 'YYYY-MM' */
    month: z.string(),
    notifiedStage: SystemCostLimitStageSchema,
    updatedAt: z.string(), // ISO 8601
});
// ========================================
// コスト上限 API レスポンススキーマ
// ========================================
/**
 * GET /cost-limit/status レスポンス
 *
 * systemLimitState は一般利用者向けのため到達段階のみを載せ、
 * システム全体の金額・上限額・残額・使用率は一切含めない。
 */
export const CostLimitStatusResponseSchema = z.object({
    currentCost: z.number(),
    limit: z.number().nullable(),
    remaining: z.number().nullable(),
    usagePercent: z.number().nullable(),
    period: CostLimitPeriodSchema,
    isDefault: z.boolean(),
    /** 本機能を含まないバックエンドの応答でも解析できるよう任意 + 既定値とする */
    systemLimitState: SystemCostLimitStageSchema.optional().default('normal'),
});
/**
 * GET /admin/cost-limits レスポンス
 */
export const CostLimitsListResponseSchema = z.object({
    defaultLimit: CostLimitSettingSchema.nullable(),
    userLimits: z.array(UserCostLimitEntrySchema),
    /** 本機能を含まないバックエンドの応答でも解析できるよう任意 + 既定値とする */
    systemLimit: CostLimitSettingSchema.nullable().optional().default(null),
    /** 省略時は「消化状況を取得できなかった」と同じ表示へ倒す */
    systemStatus: SystemCostLimitStatusSchema.optional(),
});
/**
 * GET /admin/cost-limits/users/:email レスポンス
 */
export const UserCostLimitDetailResponseSchema = z.object({
    email: z.string(),
    effectiveLimit: z.number().nullable(),
    userLimit: CostLimitSettingSchema.nullable(),
    defaultLimit: CostLimitSettingSchema.nullable(),
    currentCost: z.number(),
    usagePercent: z.number().nullable(),
    period: CostLimitPeriodSchema,
});
// ========================================
// コスト上限 API リクエストスキーマ
// ========================================
/**
 * PUT /admin/cost-limits/default, PUT /admin/cost-limits/users/:email リクエスト
 */
export const SetCostLimitRequestSchema = z.object({
    limitUSD: CostLimitUSDSchema,
});
//# sourceMappingURL=cost-limit-schemas.js.map